/**
 * Appointment Service
 *
 * Staff/admin API calls for appointments, schedules, overrides, and slots
 */

import { get, post } from '@/lib/api'
import { API_ROUTES } from '@/config/constants'
import type {
  Appointment,
  AppointmentWithDetails,
  CreateAppointmentData,
  AppointmentSearchFilters,
  RescheduleData,
  ProviderSchedule,
  CreateProviderScheduleData,
  UpdateProviderScheduleData,
  ProviderScheduleSearchFilters,
  ScheduleOverride,
  CreateScheduleOverrideData,
  TimeSlot,
  AvailableDay,
  MiAgenda,
} from '@/domain/appointment/types'

// =============================================================================
// Response types (backend format)
// =============================================================================

interface AppointmentListResponse {
  appointments: AppointmentWithDetails[]
  total: number
  limit: number
  offset: number
}

interface ScheduleListResponse {
  schedules: ProviderSchedule[]
  limit: number
  offset: number
}

// =============================================================================
// PROVIDER SCHEDULES
// =============================================================================

export async function getSchedules(
  filters?: ProviderScheduleSearchFilters
): Promise<ScheduleListResponse> {
  const params = new URLSearchParams()
  if (filters?.providerId) params.append('providerId', filters.providerId)
  if (filters?.modalityId) params.append('modalityId', filters.modalityId)
  if (filters?.dayOfWeek !== undefined) params.append('dayOfWeek', String(filters.dayOfWeek))
  if (filters?.isActive !== undefined) params.append('isActive', String(filters.isActive))
  if (filters?.institutionId) params.append('institutionId', filters.institutionId)
  if (filters?.limit) params.append('limit', String(filters.limit))
  if (filters?.offset) params.append('offset', String(filters.offset))

  const queryString = params.toString()
  const url = queryString
    ? `${API_ROUTES.APPOINTMENTS.SCHEDULES.BASE}?${queryString}`
    : API_ROUTES.APPOINTMENTS.SCHEDULES.BASE
  return get<ScheduleListResponse>(url)
}

export async function getScheduleById(id: string): Promise<ProviderSchedule> {
  return get<ProviderSchedule>(API_ROUTES.APPOINTMENTS.SCHEDULES.BY_ID(id))
}

export async function createSchedule(data: CreateProviderScheduleData): Promise<ProviderSchedule> {
  return post<ProviderSchedule>(API_ROUTES.APPOINTMENTS.SCHEDULES.BASE, data)
}

/**
 * Declara la misma franja en varios días de la semana.
 *
 * El servidor crea una fila por día, que es lo correcto porque cada día puede
 * tener horario distinto. Lo que deja de hacerse a mano es la traducción: un
 * radiólogo de lunes a viernes eran cinco formularios.
 *
 * Si alguna se pisa con lo que ya hay, no se crea ninguna y el servidor dice
 * con qué se pisa.
 */
export async function crearSemana(data: {
  providerId: string
  modalityId: string
  dias: number[]
  startTime: string
  endTime: string
  slotDurationMinutes: number
  maxConcurrent?: number
}): Promise<{ franjas: ProviderSchedule[]; creadas: number }> {
  return post<{ franjas: ProviderSchedule[]; creadas: number }>(
    API_ROUTES.APPOINTMENTS.SCHEDULES.SEMANA,
    data
  )
}

export async function updateSchedule(id: string, data: UpdateProviderScheduleData): Promise<ProviderSchedule> {
  return post<ProviderSchedule>(API_ROUTES.APPOINTMENTS.SCHEDULES.UPDATE(id), data)
}

export async function deleteSchedule(id: string): Promise<void> {
  return post(API_ROUTES.APPOINTMENTS.SCHEDULES.DELETE(id), {})
}

// =============================================================================
// SCHEDULE OVERRIDES
// =============================================================================

export async function getOverrides(
  providerId: string,
  dateFrom: string,
  dateTo: string
): Promise<{ overrides: ScheduleOverride[] }> {
  const params = new URLSearchParams({ providerId, dateFrom, dateTo })
  return get<{ overrides: ScheduleOverride[] }>(`${API_ROUTES.APPOINTMENTS.OVERRIDES.BASE}?${params.toString()}`)
}

export async function createOverride(data: CreateScheduleOverrideData): Promise<ScheduleOverride> {
  return post<ScheduleOverride>(API_ROUTES.APPOINTMENTS.OVERRIDES.BASE, data)
}

export async function deleteOverride(id: string): Promise<void> {
  return post(API_ROUTES.APPOINTMENTS.OVERRIDES.DELETE(id), {})
}

// =============================================================================
// AVAILABLE SLOTS
// =============================================================================

export async function getAvailableSlots(
  providerId: string,
  modalityId: string,
  date: string
): Promise<TimeSlot[]> {
  const params = new URLSearchParams({ providerId, modalityId, date })
  const response = await get<{ slots: TimeSlot[]; date: string }>(`${API_ROUTES.APPOINTMENTS.SLOTS}?${params.toString()}`)
  return response.slots
}

export async function getAvailableDays(
  providerId: string,
  modalityId: string,
  dateFrom: string,
  dateTo: string
): Promise<AvailableDay[]> {
  const params = new URLSearchParams({ providerId, modalityId, dateFrom, dateTo })
  const response = await get<{ days: AvailableDay[] }>(`${API_ROUTES.APPOINTMENTS.AVAILABLE_DAYS}?${params.toString()}`)
  return response.days
}

// =============================================================================
// APPOINTMENTS
// =============================================================================

export async function getAppointments(
  filters?: AppointmentSearchFilters
): Promise<{ items: AppointmentWithDetails[]; total: number; limit: number; offset: number }> {
  const params = new URLSearchParams()
  if (filters?.patientId) params.append('patientId', filters.patientId)
  if (filters?.providerId) params.append('providerId', filters.providerId)
  if (filters?.modalityId) params.append('modalityId', filters.modalityId)
  if (filters?.status) params.append('status', filters.status)
  if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom)
  if (filters?.dateTo) params.append('dateTo', filters.dateTo)
  if (filters?.bookedVia) params.append('bookedVia', filters.bookedVia)
  if (filters?.institutionId) params.append('institutionId', filters.institutionId)
  if (filters?.limit) params.append('limit', String(filters.limit))
  if (filters?.offset) params.append('offset', String(filters.offset))

  const queryString = params.toString()
  const url = queryString
    ? `${API_ROUTES.APPOINTMENTS.BASE}?${queryString}`
    : API_ROUTES.APPOINTMENTS.BASE

  const response = await get<AppointmentListResponse>(url)
  return {
    items: response.appointments,
    total: response.total,
    limit: response.limit,
    offset: response.offset,
  }
}

export async function getAppointmentById(id: string): Promise<AppointmentWithDetails> {
  return get<AppointmentWithDetails>(API_ROUTES.APPOINTMENTS.BY_ID(id))
}

export async function createAppointment(data: CreateAppointmentData): Promise<Appointment> {
  return post<Appointment>(API_ROUTES.APPOINTMENTS.BASE, data)
}

// === Status transitions ===

export async function scheduleAppointment(id: string): Promise<Appointment> {
  return post<Appointment>(API_ROUTES.APPOINTMENTS.SCHEDULE(id), {})
}

export async function confirmAppointment(id: string): Promise<Appointment> {
  return post<Appointment>(API_ROUTES.APPOINTMENTS.CONFIRM(id), {})
}

export async function checkInAppointment(id: string): Promise<Appointment> {
  return post<Appointment>(API_ROUTES.APPOINTMENTS.CHECK_IN(id), {})
}

export async function startAppointment(id: string): Promise<Appointment> {
  return post<Appointment>(API_ROUTES.APPOINTMENTS.START(id), {})
}

export async function completeAppointment(id: string): Promise<Appointment> {
  return post<Appointment>(API_ROUTES.APPOINTMENTS.COMPLETE(id), {})
}

export async function cancelAppointment(id: string, reason: string): Promise<Appointment> {
  return post<Appointment>(API_ROUTES.APPOINTMENTS.CANCEL(id), { reason })
}

export async function markNoShow(id: string): Promise<Appointment> {
  return post<Appointment>(API_ROUTES.APPOINTMENTS.NO_SHOW(id), {})
}

export async function rescheduleAppointment(id: string, data: RescheduleData): Promise<Appointment> {
  return post<Appointment>(API_ROUTES.APPOINTMENTS.RESCHEDULE(id), data)
}

export async function deleteAppointment(id: string): Promise<void> {
  return post(API_ROUTES.APPOINTMENTS.DELETE(id), {})
}

/**
 * La agenda propia del profesional.
 *
 * No recibe el id de nadie: el servidor lo saca del token. Si lo recibiera,
 * cualquiera con `appointment:view:own` leeria la agenda de otro cambiando un
 * valor en la URL.
 *
 * El rango lo manda el navegador y no lo calcula el servidor: una cita guarda
 * hora de pared de la institucion, y el servidor puede estar en otro pais.
 */
export const obtenerMiAgenda = async (desde: string, hasta: string): Promise<MiAgenda> => {
  const params = new URLSearchParams({ desde, hasta })
  return get<MiAgenda>(`${API_ROUTES.APPOINTMENTS.MI_AGENDA}?${params.toString()}`)
}
